import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/assets";
import { LogOut, Trash2, Plus, Check, Eye, Megaphone, Users, Mail, Film, Download } from "lucide-react";

const emptyArticle = { title: { en: "", fr: "" }, excerpt: { en: "", fr: "" }, body: { en: "", fr: "" }, category: "news", image: "", event_date: "", published: true };
const emptyBroadcast = { title: { en: "", fr: "" }, body: { en: "", fr: "" } };
const emptyUser = { name: "", email: "", role: "parent", send_invite: true };
const emptyEmail = { user_ids: [], subject: "", body: "" };

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState(null);
  const [galleryDraft, setGalleryDraft] = useState({ image: "", caption: { en: "", fr: "" } });
  const [broadcastDraft, setBroadcastDraft] = useState(emptyBroadcast);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [userDraft, setUserDraft] = useState(emptyUser);
  const [emailDraft, setEmailDraft] = useState(emptyEmail);
  const [sendingEmail, setSendingEmail] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, g, i, b, u] = await Promise.all([
        api.get("/admin/articles"), api.get("/gallery"), api.get("/admin/inquiries"),
        api.get("/admin/broadcasts"), api.get("/admin/users"),
      ]);
      setArticles(a.data); setGallery(g.data); setInquiries(i.data);
      setBroadcasts(b.data); setUsers(u.data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const saveArticle = async () => {
    try {
      const payload = { ...draft, event_date: draft.event_date || null };
      if (draft.id) await api.put(`/admin/articles/${draft.id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success("Saved"); setDraft(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const delArticle = async (id) => { try { await api.delete(`/admin/articles/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const addGallery = async () => {
    if (!galleryDraft.image) return toast.error("Image URL required");
    try {
      await api.post("/admin/gallery", galleryDraft); toast.success("Added");
      setGalleryDraft({ image: "", caption: { en: "", fr: "" } }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const delGallery = async (id) => { try { await api.delete(`/admin/gallery/${id}`); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const toggleInquiry = async (id) => { try { await api.patch(`/admin/inquiries/${id}`); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const delInquiry = async (id) => { try { await api.delete(`/admin/inquiries/${id}`); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };

  const sendBroadcast = async () => {
    const { title, body } = broadcastDraft;
    if (!title.en && !title.fr) return toast.error("Please add a title (EN or FR)");
    if (!body.en && !body.fr) return toast.error("Please add a message body");
    setSendingBroadcast(true);
    try {
      await api.post("/admin/broadcasts", broadcastDraft);
      toast.success("Broadcast sent to all parents");
      setBroadcastDraft(emptyBroadcast);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSendingBroadcast(false); }
  };
  const delBroadcast = async (id) => { try { await api.delete(`/admin/broadcasts/${id}`); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };

  const createUser = async () => {
    if (!userDraft.email) return toast.error("Email is required");
    try {
      const res = await api.post("/admin/users", userDraft);
      const temp = res.data.temp_password;
      toast.success(`User created${temp ? ` · temp password: ${temp}` : ""}`);
      setUserDraft(emptyUser);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const delUser = async (id) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    try { await api.delete(`/admin/users/${id}`); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const resetUserPassword = async (email) => {
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("Reset link sent (or logged on backend if no email service).");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const toggleUserSelect = (id) => {
    setEmailDraft((d) => ({ ...d, user_ids: d.user_ids.includes(id) ? d.user_ids.filter((x) => x !== id) : [...d.user_ids, id] }));
  };
  const sendEmailToParents = async () => {
    if (!emailDraft.subject.trim() || !emailDraft.body.trim()) return toast.error("Subject and body required");
    if (emailDraft.user_ids.length === 0) return toast.error("Select at least one parent");
    setSendingEmail(true);
    try {
      const res = await api.post("/admin/email-parents", emailDraft);
      toast.success(`Sent to ${res.data.sent}/${res.data.recipients} recipient(s)`);
      setEmailDraft(emptyEmail);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSendingEmail(false); }
  };

  const inp = "w-full h-11 px-3 border border-icdh-navy/20 focus:border-icdh-red outline-none bg-white text-icdh-navy text-sm";
  const tabs = [
    ["articles", "News & Events"], ["gallery", "Gallery"],
    ["inquiries", `Inquiries (${inquiries.length})`], ["broadcasts", "Broadcasts"],
    ["users", `Users (${users.length})`], ["email", "Email Parents"],
  ];

  return (
    <div className="min-h-screen bg-icdh-surface text-icdh-navy">
      <header className="bg-icdh-navy text-icdh-white sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="ICDH" className="h-9 w-9 object-contain bg-white rounded-sm p-0.5" />
            <span className="font-extrabold tracking-tight text-sm md:text-base">Institution Chrétienne d'Haïti · Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/library" data-testid="admin-library-link" className="text-sm flex items-center gap-2 hover:text-icdh-red"><Film size={16} />Bibliothèque STEM</Link>
            <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="text-sm flex items-center gap-2 hover:text-icdh-red"><Eye size={16} />View site</a>
            <button onClick={doLogout} data-testid="admin-logout" className="text-sm flex items-center gap-2 hover:text-icdh-red"><LogOut size={16} />Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 py-8">
        <div className="flex flex-wrap gap-px border border-icdh-navy/15 w-fit mb-8">
          {tabs.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-6 h-11 text-sm font-medium transition-colors ${tab === k ? "bg-icdh-navy text-icdh-white" : "bg-white text-icdh-navy hover:bg-icdh-surface"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "articles" && (
          <div>
            <button onClick={() => setDraft(emptyArticle)} data-testid="new-article-btn"
              className="mb-6 h-11 px-5 bg-icdh-red text-white text-xs font-bold uppercase tracking-[0.15em] flex items-center gap-2 hover:bg-icdh-navy transition-colors"><Plus size={16} />New Article</button>
            <div className="grid gap-3">
              {articles.map((a) => (
                <div key={a.id} data-testid={`article-row-${a.id}`} className="bg-white border border-icdh-navy/10 p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <img src={a.image} alt="" className="h-14 w-20 object-cover flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{a.title.en} <span className="text-icdh-navy/40">/ {a.title.fr}</span></p>
                      <span className="text-xs uppercase tracking-wider text-icdh-red">{a.category}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setDraft({ ...a, event_date: a.event_date || "" })} data-testid={`edit-${a.id}`} className="h-9 px-4 border border-icdh-navy/20 text-sm hover:bg-icdh-navy hover:text-white transition-colors">Edit</button>
                    <button onClick={() => delArticle(a.id)} data-testid={`del-article-${a.id}`} className="h-9 w-9 flex items-center justify-center border border-icdh-navy/20 hover:bg-icdh-red hover:text-white transition-colors"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div>
            <div className="bg-white border border-icdh-navy/10 p-5 mb-6 grid md:grid-cols-4 gap-3 items-end">
              <input className={inp} placeholder="Image URL" data-testid="gallery-url" value={galleryDraft.image} onChange={(e) => setGalleryDraft({ ...galleryDraft, image: e.target.value })} />
              <input className={inp} placeholder="Caption (EN)" value={galleryDraft.caption.en} onChange={(e) => setGalleryDraft({ ...galleryDraft, caption: { ...galleryDraft.caption, en: e.target.value } })} />
              <input className={inp} placeholder="Caption (FR)" value={galleryDraft.caption.fr} onChange={(e) => setGalleryDraft({ ...galleryDraft, caption: { ...galleryDraft.caption, fr: e.target.value } })} />
              <button onClick={addGallery} data-testid="add-gallery-btn" className="h-11 bg-icdh-red text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors">Add Photo</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {gallery.map((g) => (
                <div key={g.id} className="relative group">
                  <img src={g.image} alt="" className="w-full aspect-square object-cover" />
                  <button onClick={() => delGallery(g.id)} data-testid={`del-gallery-${g.id}`} className="absolute top-2 right-2 h-8 w-8 bg-icdh-red text-white flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "inquiries" && (
          <div className="grid gap-3">
            {inquiries.length === 0 && <p className="text-icdh-navy/50">No inquiries yet.</p>}
            {inquiries.map((q) => (
              <div key={q.id} data-testid={`inquiry-${q.id}`} className={`bg-white border p-4 md:p-5 ${q.handled ? "border-icdh-navy/10 opacity-60" : "border-icdh-red/40"}`}>
                <div className="flex flex-col md:flex-row items-start md:justify-between gap-4">
                  <div className="min-w-0 flex-1 w-full">
                    <p className="font-semibold">{q.name} <span className="text-xs uppercase tracking-wider text-icdh-red ml-2">{q.type}</span></p>
                    <p className="text-sm text-icdh-navy/60 break-words">{q.email} {q.phone && `· ${q.phone}`} {q.program && `· ${q.program}`}</p>
                    {q.student_name && <p className="mt-2 text-sm text-icdh-navy/85"><b>Student:</b> {q.student_name}{q.student_class ? ` · ${q.student_class}` : ""}</p>}
                    {q.message && <p className="mt-3 text-sm text-icdh-navy/80 whitespace-pre-wrap">{q.message}</p>}
                    {(q.student_photo_url || q.parent_photo_url || (q.document_urls && q.document_urls.length > 0)) && (
                      <div className="mt-4 grid grid-cols-3 sm:flex sm:flex-wrap gap-3" data-testid={`inquiry-attachments-${q.id}`}>
                        {q.student_photo_url && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${q.student_photo_url}`} target="_blank" rel="noreferrer" className="group block" title="Student photo">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${q.student_photo_url}`} alt="Student" className="w-full aspect-square sm:h-28 sm:w-28 object-cover border border-icdh-navy/15 group-hover:border-icdh-red" />
                            <p className="mt-1 text-[11px] uppercase tracking-wider text-icdh-navy/60 text-center">Student</p>
                          </a>
                        )}
                        {q.parent_photo_url && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${q.parent_photo_url}`} target="_blank" rel="noreferrer" className="group block" title="Parent photo">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${q.parent_photo_url}`} alt="Parent" className="w-full aspect-square sm:h-28 sm:w-28 object-cover border border-icdh-navy/15 group-hover:border-icdh-red" />
                            <p className="mt-1 text-[11px] uppercase tracking-wider text-icdh-navy/60 text-center">Parent</p>
                          </a>
                        )}
                        {q.document_urls && q.document_urls.map((u, i) => (
                          <a key={u} href={`${process.env.REACT_APP_BACKEND_URL}${u}`} target="_blank" rel="noreferrer" className="w-full aspect-square sm:h-28 sm:w-28 border border-icdh-navy/15 hover:border-icdh-red flex flex-col items-center justify-center text-icdh-navy/70 hover:text-icdh-red transition-colors bg-icdh-surface">
                            <span className="text-sm font-bold">DOC {i + 1}</span>
                            <span className="text-[10px] uppercase tracking-wider mt-1">Open</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0 w-full md:w-auto">
                    {q.type === "admission" && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries/${q.id}/export?auth=${encodeURIComponent(localStorage.getItem("icdh_token") || "")}`}
                         target="_blank" rel="noreferrer"
                         data-testid={`export-inquiry-${q.id}`}
                         title="Download ZIP of this admission (photos + documents)"
                         className="flex-1 md:flex-none h-11 px-3 md:px-4 flex items-center justify-center gap-1.5 bg-icdh-navy text-white hover:bg-icdh-red transition-colors text-xs font-bold uppercase tracking-wider">
                        <Download size={16} /><span>ZIP</span>
                      </a>
                    )}
                    <button onClick={() => toggleInquiry(q.id)} aria-label="Mark handled" data-testid={`toggle-inquiry-${q.id}`} className="flex-1 md:flex-none h-11 min-w-[44px] px-3 flex items-center justify-center border border-icdh-navy/20 hover:bg-icdh-navy hover:text-white transition-colors"><Check size={18} /></button>
                    <button onClick={() => delInquiry(q.id)} aria-label="Delete inquiry" data-testid={`del-inquiry-${q.id}`} className="flex-1 md:flex-none h-11 min-w-[44px] px-3 flex items-center justify-center border border-icdh-navy/20 hover:bg-icdh-red hover:text-white transition-colors"><Trash2 size={18} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "broadcasts" && (
          <div data-testid="broadcasts-panel">
            <div className="bg-white border border-icdh-navy/10 p-6 mb-8">
              <div className="flex items-center gap-3">
                <Megaphone className="text-icdh-red" />
                <h3 className="font-serif text-2xl">Send a note to all parents</h3>
              </div>
              <p className="mt-2 text-sm text-icdh-navy/60">This message will appear inside every parent's portal. Fill in both languages when possible.</p>
              <div className="mt-6 grid gap-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <input className={inp} placeholder="Title (EN)" data-testid="bc-title-en" value={broadcastDraft.title.en}
                    onChange={(e) => setBroadcastDraft({ ...broadcastDraft, title: { ...broadcastDraft.title, en: e.target.value } })} />
                  <input className={inp} placeholder="Titre (FR)" data-testid="bc-title-fr" value={broadcastDraft.title.fr}
                    onChange={(e) => setBroadcastDraft({ ...broadcastDraft, title: { ...broadcastDraft.title, fr: e.target.value } })} />
                </div>
                <textarea className={`${inp} h-28 py-2`} placeholder="Message (EN)" data-testid="bc-body-en" value={broadcastDraft.body.en}
                  onChange={(e) => setBroadcastDraft({ ...broadcastDraft, body: { ...broadcastDraft.body, en: e.target.value } })} />
                <textarea className={`${inp} h-28 py-2`} placeholder="Message (FR)" data-testid="bc-body-fr" value={broadcastDraft.body.fr}
                  onChange={(e) => setBroadcastDraft({ ...broadcastDraft, body: { ...broadcastDraft.body, fr: e.target.value } })} />
                <button onClick={sendBroadcast} disabled={sendingBroadcast} data-testid="bc-send"
                  className="h-11 px-6 bg-icdh-red text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors self-start disabled:opacity-60">
                  {sendingBroadcast ? "..." : "Send to all parents"}
                </button>
              </div>
            </div>

            <div className="grid gap-3">
              {broadcasts.length === 0 && <p className="text-icdh-navy/50">No broadcasts sent yet.</p>}
              {broadcasts.map((b) => (
                <div key={b.id} data-testid={`bc-row-${b.id}`} className="bg-white border border-icdh-navy/10 p-5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold">{b.title.en || b.title.fr}</p>
                    <p className="text-xs uppercase tracking-wider text-icdh-red mt-1">{new Date(b.created_at).toLocaleString()}</p>
                    <p className="mt-3 text-sm text-icdh-navy/70 whitespace-pre-wrap line-clamp-3">{b.body.en || b.body.fr}</p>
                  </div>
                  <button onClick={() => delBroadcast(b.id)} data-testid={`bc-del-${b.id}`} className="h-9 w-9 flex-shrink-0 flex items-center justify-center border border-icdh-navy/20 hover:bg-icdh-red hover:text-white transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "users" && (
          <div data-testid="users-panel">
            <div className="bg-white border border-icdh-navy/10 p-6 mb-8">
              <div className="flex items-center gap-3">
                <Users className="text-icdh-red" />
                <h3 className="font-serif text-2xl">Add a user</h3>
              </div>
              <p className="mt-2 text-sm text-icdh-navy/60">Create a login for a parent, student or admin. If "send invite" is on, they receive an email with their temporary password.</p>
              <div className="mt-6 grid gap-3">
                <div className="grid md:grid-cols-3 gap-3">
                  <input className={inp} placeholder="Full name" data-testid="user-name" value={userDraft.name} onChange={(e) => setUserDraft({ ...userDraft, name: e.target.value })} />
                  <input className={inp} placeholder="Email" type="email" data-testid="user-email" value={userDraft.email} onChange={(e) => setUserDraft({ ...userDraft, email: e.target.value })} />
                  <select className={inp} data-testid="user-role" value={userDraft.role} onChange={(e) => setUserDraft({ ...userDraft, role: e.target.value })}>
                    <option value="parent">Parent</option>
                    <option value="student">Student</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <label className="text-sm text-icdh-navy/70 inline-flex items-center gap-2">
                  <input type="checkbox" checked={userDraft.send_invite} onChange={(e) => setUserDraft({ ...userDraft, send_invite: e.target.checked })} /> Email an invite with temporary password
                </label>
                <button onClick={createUser} data-testid="user-create" className="h-11 px-6 bg-icdh-red text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors self-start">
                  Create user
                </button>
              </div>
            </div>

            <div className="grid gap-2">
              {users.length === 0 && <p className="text-icdh-navy/50">No users yet.</p>}
              {users.map((u) => (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="bg-white border border-icdh-navy/10 p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{u.name || u.email} <span className="ml-2 text-xs uppercase tracking-wider text-icdh-red">{u.role}</span></p>
                    <p className="text-xs text-icdh-navy/60 truncate">{u.email}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => resetUserPassword(u.email)} data-testid={`user-reset-${u.id}`} title="Send password reset link" className="h-9 px-3 border border-icdh-navy/20 hover:bg-icdh-navy hover:text-white transition-colors text-xs uppercase tracking-wider">Reset PW</button>
                    <button onClick={() => delUser(u.id)} data-testid={`user-del-${u.id}`} className="h-9 w-9 flex items-center justify-center border border-icdh-navy/20 hover:bg-icdh-red hover:text-white transition-colors"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "email" && (
          <div data-testid="email-panel" className="grid lg:grid-cols-[1fr,340px] gap-6">
            <div className="bg-white border border-icdh-navy/10 p-6">
              <div className="flex items-center gap-3">
                <Mail className="text-icdh-red" />
                <h3 className="font-serif text-2xl">Email parents</h3>
              </div>
              <p className="mt-2 text-sm text-icdh-navy/60">
                Send a reminder or note directly to the inbox of one or more parents. Broadcasts appear in the portal; this one lands in their email.
              </p>
              <div className="mt-6 grid gap-3">
                <input className={inp} placeholder="Subject" data-testid="email-subject" value={emailDraft.subject}
                  onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })} />
                <textarea className={`${inp} h-52 py-2`} placeholder="Message body (HTML allowed, e.g. <b>bold</b>, <br/>)" data-testid="email-body" value={emailDraft.body}
                  onChange={(e) => setEmailDraft({ ...emailDraft, body: e.target.value })} />
                <p className="text-xs text-icdh-navy/60">Selected: <b>{emailDraft.user_ids.length}</b> recipient(s)</p>
                <button onClick={sendEmailToParents} disabled={sendingEmail} data-testid="email-send"
                  className="h-11 px-6 bg-icdh-red text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors self-start disabled:opacity-60">
                  {sendingEmail ? "..." : `Send to ${emailDraft.user_ids.length || 0} parent(s)`}
                </button>
              </div>
            </div>

            <div className="bg-white border border-icdh-navy/10 p-4 max-h-[560px] overflow-y-auto">
              <p className="text-xs uppercase tracking-wider text-icdh-red mb-3">Select recipients</p>
              {users.filter((u) => u.role !== "admin").length === 0 && <p className="text-icdh-navy/50 text-sm">Add parents/students in the Users tab first.</p>}
              {users.filter((u) => u.role !== "admin").map((u) => (
                <label key={u.id} data-testid={`recipient-${u.id}`} className="flex items-center gap-3 py-2 border-b border-icdh-navy/10 cursor-pointer hover:bg-icdh-surface">
                  <input type="checkbox" checked={emailDraft.user_ids.includes(u.id)} onChange={() => toggleUserSelect(u.id)} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{u.name || u.email}</p>
                    <p className="text-xs text-icdh-navy/50 truncate">{u.email} · {u.role}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {draft && (
        <div className="fixed inset-0 z-50 bg-icdh-navy/60 flex items-center justify-center p-4" onClick={() => setDraft(null)}>
          <div data-testid="article-editor" className="bg-white w-full max-w-2xl max-h-[88vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-6">{draft.id ? "Edit" : "New"} Article</h3>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="Title (EN)" data-testid="draft-title-en" value={draft.title.en} onChange={(e) => setDraft({ ...draft, title: { ...draft.title, en: e.target.value } })} />
                <input className={inp} placeholder="Titre (FR)" value={draft.title.fr} onChange={(e) => setDraft({ ...draft, title: { ...draft.title, fr: e.target.value } })} />
                <input className={inp} placeholder="Excerpt (EN)" value={draft.excerpt.en} onChange={(e) => setDraft({ ...draft, excerpt: { ...draft.excerpt, en: e.target.value } })} />
                <input className={inp} placeholder="Extrait (FR)" value={draft.excerpt.fr} onChange={(e) => setDraft({ ...draft, excerpt: { ...draft.excerpt, fr: e.target.value } })} />
              </div>
              <textarea className={`${inp} h-24 py-2`} placeholder="Body (EN)" value={draft.body.en} onChange={(e) => setDraft({ ...draft, body: { ...draft.body, en: e.target.value } })} />
              <textarea className={`${inp} h-24 py-2`} placeholder="Corps (FR)" value={draft.body.fr} onChange={(e) => setDraft({ ...draft, body: { ...draft.body, fr: e.target.value } })} />
              <input className={inp} placeholder="Image URL" data-testid="draft-image" value={draft.image} onChange={(e) => setDraft({ ...draft, image: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <select className={inp} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                  <option value="news">News</option>
                  <option value="event">Event</option>
                </select>
                <input type="date" className={inp} value={draft.event_date || ""} onChange={(e) => setDraft({ ...draft, event_date: e.target.value })} />
              </div>
              <div className="flex gap-3 mt-2">
                <button onClick={saveArticle} data-testid="save-article-btn" className="h-11 px-6 bg-icdh-red text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors">Save</button>
                <button onClick={() => setDraft(null)} className="h-11 px-6 border border-icdh-navy/20 text-sm hover:bg-icdh-surface transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
